import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import styled, { ThemeProvider } from 'styled-components'
import Input from '../Input'
import Chat, { useGetSocketMessage } from '../Chat'
import Placeholder from '../Placeholder/'
import Examples from '../Examples'
import { GlobalVariables } from 'Globals/GlobalVariables'
import { useEffect, useState } from 'react'
import { useTransition, animated, config } from '@react-spring/web'
import { MdElectricBolt } from 'react-icons/md'
import { IoIosAdd } from 'react-icons/io'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { getTheme } from '../../theme/config'

const MainComponent = ({
  tenant,
  colorMode,
  properties,
  trackEvent,
}: MicroFrontendProps) => {
  const theme = getTheme(colorMode, tenant)
  const [chatMessages, setChatMessages] = useState<ChatInterface[]>([])
  const { sendMessage, message, isConnecting, isLoading, error } =
    useGetSocketMessage()

  const transitions = useTransition(isLoading, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.molasses,
  })

  const handleNewUserMessage = (text: string) => {
    if (isLoading) return

    const date = new Date()

    // Display the question in the chat
    setChatMessages((prevChatMessages) => [
      ...prevChatMessages,
      { text, id: prevChatMessages.length, type: 'user', date: date },
    ])

    sendMessage(text)
  }

  useEffect(() => {
    if (message) {
      setChatMessages((prevChatMessages) => [...prevChatMessages, message])
    }
  }, [message])

  return (
    <AppContainer $colorMode={colorMode}>
      <ThemeProvider theme={theme}>
        <Chat messages={chatMessages} />
        <>{chatMessages.length === 0 && <Examples />}</>
        <>{isConnecting && <UIMessage>Connection en cours...</UIMessage>}</>
        <>{isLoading && !isConnecting && <Placeholder />}</>
        <>{error && <TextWidth><UIMessage>Le service n'est pas accessible pour le moment. Réessayez plus tard!</UIMessage></TextWidth>}</>
        {!isConnecting && (
          <UserBox>
            <TextWidth>
              <Menu>
                <Message>
                  <Icon $isLoading={isLoading} />
                  {transitions((style, items) =>
                    items ? (
                      <MessageContent style={style}>
                        Nous composons une réponse.
                      </MessageContent>
                    ) : (
                      <MessageContent style={style}>
                        {error
                          ? "Une erreur s'est produite. Veuillez réessayer."
                          : chatMessages.length === 0
                          ? 'Commencer la conversation'
                          : 'Continuer la conversation'}
                      </MessageContent>
                    )
                  )}
                </Message>
              </Menu>
              <InputContainer>
                <Input onEnter={handleNewUserMessage} />
              </InputContainer>
              <Footer>
                <Notes>
                  <a href='https://www.tamedia.ch'>En savoir plus</a>
                  {/* <RiQuestionLine /> */}
                </Notes>
                {chatMessages.length > 0 && (
                  <Button
                    onClick={() => {
                      setChatMessages([])
                    }}
                  >
                    <AddIcon />
                    Nouvelle conversation
                  </Button>
                )}
              </Footer>
            </TextWidth>
          </UserBox>
        )}
      </ThemeProvider>
    </AppContainer>
  )
}

const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  margin-bottom: 3rem;
`

const Menu = styled.div``

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
`

const UserBox = styled.div`
  background-color: ${({ theme }) => theme.colorGreyLight};
  padding: 3rem 0 2rem 0;
  border-top: 1px solid ${({ theme }) => theme.colorGrey};
`

const Message = styled.div`
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  position: relative;
`

const UIMessage = styled.div`
  font-size: 1.5rem;
  position: relative;
  text-align: center;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  color: ${({ theme }) => theme.colorTextInverted};
  background-color: ${({ theme }) => theme.colorText};
`

const Button = styled.div`
  font-size: 1.35rem;
  background-color: ${({ theme }) => theme.colorText};
  color: ${({ theme }) => theme.colorTextInverted};
  padding: 0.85rem 1.25rem;
  -webkit-font-smoothing: antialiased;
  line-height: 1;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  display: flex;
  align-items: center;
  white-space: nowrap;

  &:hover {
    opacity: 0.75;
  }
`

const AddIcon = styled(IoIosAdd)`
  font-size: 2rem;
  margin-right: 0.25rem;
`

const InputContainer = styled.div`
  margin-top: 0.65rem;
`

const Icon = styled(MdElectricBolt)<{ $isLoading: boolean }>`
  color: ${({ theme }) => theme.colorMain};
  font-size: 2rem;
  animation: ${({ $isLoading }) =>
    $isLoading ? 'spin 1s linear infinite' : 'none'};

  @keyframes spin {
    from {
      opacity: 1;
    }

    to {
      opacity: 0.25;
    }
  }
`

const MessageContent = styled(animated.div)`
  position: absolute;
  left: 2.5rem;
  line-height: 1;
`

const Notes = styled.div`
  font-size: 1.35rem;
  line-height: 1;
  display: block;
  white-space: nowrap;

  a {
    color: ${({ theme }) => theme.colorGreyDark};
  }
`

export default MainComponent
